import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faCheckCircle, faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import {
  fetchPromptRuns,
  fetchPromptDefinitions,
  fetchBatches,
  renamePromptRun,
  deletePromptRun,
  fetchRunResults,
} from "./skillsProfileApi";
import "./skillsProfile.css";

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const groupResultsBySession = (results) => {
  const groups = new Map();
  (results || []).forEach((r) => {
    const sid = r.session_id || "unknown";
    if (!groups.has(sid)) groups.set(sid, []);
    groups.get(sid).push(r);
  });
  return Array.from(groups.entries());
};

export const LlmTags = () => {
  const [runs, setRuns] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);

  // Row edit state
  const [editingRunId, setEditingRunId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [savingRename, setSavingRename] = useState(false);

  // Row delete confirm state
  const [deletingRunId, setDeletingRunId] = useState(null);

  // Detail view state (Scope 4)
  const [expandedRunId, setExpandedRunId] = useState(null);
  const [runResultsCache, setRunResultsCache] = useState({});

  const handleToggleRunDetail = async (run) => {
    if (expandedRunId === run.id) {
      setExpandedRunId(null);
      return;
    }
    setExpandedRunId(run.id);
    if (!runResultsCache[run.id]?.data && !runResultsCache[run.id]?.loading) {
      setRunResultsCache((prev) => ({
        ...prev,
        [run.id]: { loading: true, error: null, data: null },
      }));
      try {
        const res = await fetchRunResults(run.id);
        const results = Array.isArray(res) ? res : res?.results || [];
        setRunResultsCache((prev) => ({
          ...prev,
          [run.id]: { loading: false, error: null, data: results },
        }));
      } catch (err) {
        setRunResultsCache((prev) => ({
          ...prev,
          [run.id]: {
            loading: false,
            error: err?.detail || err?.message || "Failed to load run results.",
            data: null,
          },
        }));
      }
    }
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [runsData, promptsData, batchesData] = await Promise.all([
        fetchPromptRuns(),
        fetchPromptDefinitions().catch(() => []),
        fetchBatches().catch(() => []),
      ]);
      setRuns(runsData);
      setPrompts(promptsData);
      setBatches(batchesData);
    } catch (err) {
      setFeedback({
        type: "danger",
        message: `Failed to load LLM tag runs: ${err?.detail || err?.message || "Unknown error"}`,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const promptMap = useMemo(() => {
    const map = new Map();
    prompts.forEach((p) => map.set(p.id, p));
    return map;
  }, [prompts]);

  const batchMap = useMemo(() => {
    const map = new Map();
    batches.forEach((b) => map.set(b.id, b));
    return map;
  }, [batches]);

  const getRunDisplayName = (run) => {
    if (run.display_name && run.display_name.trim()) return run.display_name;
    const prompt = promptMap.get(run.prompt_id);
    if (prompt?.name) return prompt.name;
    return `Run ${run.id?.slice?.(0, 8) || run.id}`;
  };

  const getPromptLabel = (run) => {
    const prompt = promptMap.get(run.prompt_id);
    if (!prompt) return "Unknown prompt";
    return prompt.model ? `${prompt.name} (${prompt.model})` : prompt.name;
  };

  const getBatchCoverage = (run) => {
    if (!run.batch_ids || run.batch_ids.length === 0) return "No batches";
    const names = run.batch_ids.map((id) => batchMap.get(id)?.name || id.slice(0, 8));
    return names.join(", ");
  };

  const renderStatus = (status) => {
    switch (status) {
      case "pending":
        return (
          <span className="sp-run-status sp-run-queued" data-testid="status-pending">
            <span className="sp-run-dot" /> Queued
          </span>
        );
      case "running":
        return (
          <span className="sp-run-status sp-run-running" data-testid="status-running">
            <span className="sp-run-dot" /> Running
          </span>
        );
      case "completed":
        return (
          <span className="sp-run-status sp-run-done" data-testid="status-completed">
            <span className="sp-run-dot" /> Done
          </span>
        );
      case "cost_exceeded":
        return (
          <span className="sp-run-status sp-run-failed" data-testid="status-cost-exceeded">
            <span className="sp-run-dot" /> Cost exceeded
          </span>
        );
      case "failed":
      default:
        return (
          <span className="sp-run-status sp-run-failed" data-testid="status-failed">
            <span className="sp-run-dot" /> Failed
          </span>
        );
    }
  };

  const handleStartRename = (run) => {
    setDeletingRunId(null);
    setEditingRunId(run.id);
    setEditingName(getRunDisplayName(run));
  };

  const handleCancelRename = () => {
    setEditingRunId(null);
    setEditingName("");
  };

  const handleSaveRename = async (runId, nameToSave) => {
    const trimmed = (nameToSave ?? editingName).trim();
    if (!trimmed) return;

    setSavingRename(true);
    try {
      const updated = await renamePromptRun(runId, trimmed);
      setRuns((prev) =>
        prev.map((r) => (r.id === runId ? { ...r, display_name: updated?.display_name || trimmed } : r))
      );
      setEditingRunId(null);
      setEditingName("");
      setFeedback({
        type: "success",
        message: `Run renamed to "${trimmed}".`,
      });
    } catch (err) {
      setFeedback({
        type: "danger",
        message: `Failed to rename run: ${err?.detail || err?.message || "Server error"}`,
      });
    } finally {
      setSavingRename(false);
    }
  };

  const handleStartDelete = (runId) => {
    setEditingRunId(null);
    setDeletingRunId(runId);
  };

  const handleCancelDelete = () => {
    setDeletingRunId(null);
  };

  const handleConfirmDelete = async (run) => {
    const runName = getRunDisplayName(run);
    try {
      await deletePromptRun(run.id);
      setRuns((prev) => prev.filter((r) => r.id !== run.id));
      setDeletingRunId(null);
      setFeedback({
        type: "success",
        message: `Prompt run "${runName}" was deleted.`,
      });
    } catch (err) {
      setFeedback({
        type: "danger",
        message: `Failed to delete run: ${err?.detail || err?.message || "Server error"}`,
      });
    }
  };

  return (
    <div className="sp-page" data-testid="skills-profile-llm-tags">
      <div className="sp-page-header">
        <h1 className="sp-page-title">LLM Tags</h1>
        <p className="sp-page-sub">
          Every prompt run that has produced tags. Rename a run so it reads clearly in the SME Review dropdown, or delete one that's no longer needed.
        </p>
      </div>

      {feedback && (
        <div className={`sp-alert sp-alert-${feedback.type}`} role="alert" data-testid="llm-tags-feedback">
          <FontAwesomeIcon
            icon={feedback.type === "success" ? faCheckCircle : faExclamationCircle}
            style={{ marginTop: "3px" }}
          />
          <div>{feedback.message}</div>
        </div>
      )}

      <div className="sp-card" style={{ padding: "0 0 16px 0", overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: "40px", textAlign: "center", color: "#8c959f" }} data-testid="llm-tags-loading">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
            <div style={{ marginTop: "8px" }}>Loading runs…</div>
          </div>
        ) : runs.length === 0 ? (
          <div style={{ padding: "40px", textAlign: "center", color: "#8c959f" }} data-testid="llm-tags-empty">
            No LLM tag runs found.
          </div>
        ) : (
          <table className="sp-llm-tags-table" data-testid="llm-tags-table">
            <thead>
              <tr>
                <th>Run Name</th>
                <th>Prompt / Model</th>
                <th>Batches</th>
                <th>Status</th>
                <th>Items Tagged</th>
                <th>Dropped</th>
                <th>Cost</th>
                <th>Created</th>
                <th style={{ minWidth: "160px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => {
                const isEditing = editingRunId === run.id;
                const isDeleting = deletingRunId === run.id;
                const isExpanded = expandedRunId === run.id;
                const runName = getRunDisplayName(run);
                const droppedCount =
                  run.quotes_dropped_unmatched != null || run.tags_dropped_off_vocab != null
                    ? (run.quotes_dropped_unmatched || 0) + (run.tags_dropped_off_vocab || 0)
                    : null;
                const cache = runResultsCache[run.id];

                if (isDeleting) {
                  return (
                    <tr key={run.id} className="sp-confirm-row" data-testid={`run-confirm-delete-${run.id}`}>
                      <td colSpan={8} className="sp-confirm-text">
                        Delete "{runName}"? This removes it from the LLM Tags dropdown in SME Review. This can't be undone.
                      </td>
                      <td>
                        <div className="sp-row-actions">
                          <button
                            type="button"
                            className="sp-btn sp-btn-danger sp-btn-sm"
                            data-testid={`confirm-delete-btn-${run.id}`}
                            onClick={() => handleConfirmDelete(run)}
                          >
                            Delete
                          </button>
                          <button
                            type="button"
                            className="sp-btn sp-btn-ghost sp-btn-sm"
                            data-testid={`cancel-delete-btn-${run.id}`}
                            onClick={handleCancelDelete}
                          >
                            Cancel
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }

                return (
                  <React.Fragment key={run.id}>
                    <tr data-testid={`run-row-${run.id}`}>
                      <td className="run-name-cell">
                        {isEditing ? (
                          <input
                            type="text"
                            className="sp-control sp-rename-input"
                            data-testid={`rename-input-${run.id}`}
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            autoFocus
                            disabled={savingRename}
                          />
                        ) : (
                          <button
                            type="button"
                            className="sp-link-btn sp-run-name-btn"
                            data-testid={`run-name-btn-${run.id}`}
                            onClick={() => handleToggleRunDetail(run)}
                            title="Click to view run results"
                            style={{
                              background: "none",
                              border: "none",
                              padding: 0,
                              textAlign: "left",
                              fontWeight: 600,
                              color: "#0969da",
                              cursor: "pointer",
                              textDecoration: "underline",
                              font: "inherit",
                            }}
                          >
                            <span data-testid={`run-name-${run.id}`}>{runName}</span>
                          </button>
                        )}
                      </td>
                      <td>{getPromptLabel(run)}</td>
                      <td>{getBatchCoverage(run)}</td>
                      <td>{renderStatus(run.status)}</td>
                      <td>{run.quotes_kept != null ? run.quotes_kept : "—"}</td>
                      <td data-testid={`run-dropped-${run.id}`}>
                        {droppedCount != null ? droppedCount : "—"}
                      </td>
                      <td>{run.cost_usd != null ? `$${run.cost_usd.toFixed(2)}` : "—"}</td>
                      <td>{formatDate(run.created_at)}</td>
                      <td>
                        {isEditing ? (
                          <div className="sp-row-actions">
                            <button
                              type="button"
                              className="sp-btn sp-btn-primary sp-btn-sm"
                              data-testid={`save-rename-btn-${run.id}`}
                              onClick={() => handleSaveRename(run.id, editingName)}
                              disabled={savingRename || !editingName.trim()}
                            >
                              {savingRename ? (
                                <>
                                  <FontAwesomeIcon icon={faSpinner} spin /> Saving…
                                </>
                              ) : (
                                "Save"
                              )}
                            </button>
                            <button
                              type="button"
                              className="sp-btn sp-btn-ghost sp-btn-sm"
                              data-testid={`cancel-rename-btn-${run.id}`}
                              onClick={handleCancelRename}
                              disabled={savingRename}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="sp-row-actions">
                            <button
                              type="button"
                              className="sp-btn sp-btn-ghost sp-btn-sm"
                              data-testid={`rename-btn-${run.id}`}
                              onClick={() => handleStartRename(run)}
                            >
                              Rename
                            </button>
                            <button
                              type="button"
                              className="sp-btn sp-btn-danger-ghost sp-btn-sm"
                              data-testid={`delete-btn-${run.id}`}
                              onClick={() => handleStartDelete(run.id)}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="sp-run-detail-row" data-testid={`run-detail-row-${run.id}`}>
                        <td colSpan={9} style={{ padding: "16px 20px", background: "#f6f8fa", borderBottom: "1px solid #d0d7de" }}>
                          {cache?.loading ? (
                            <div style={{ textAlign: "center", padding: "16px", color: "#6e7781" }} data-testid={`run-detail-loading-${run.id}`}>
                              <FontAwesomeIcon icon={faSpinner} spin /> Loading results…
                            </div>
                          ) : cache?.error ? (
                            <div className="sp-alert sp-alert-danger" data-testid={`run-detail-error-${run.id}`}>
                              {cache.error}
                            </div>
                          ) : cache?.data && cache.data.length === 0 ? (
                            <div style={{ padding: "12px 16px", color: "#57606a", fontStyle: "italic" }} data-testid={`run-detail-empty-${run.id}`}>
                              no results ({droppedCount != null ? droppedCount : "—"} dropped)
                            </div>
                          ) : cache?.data ? (
                            <div className="sp-run-detail-content" data-testid={`run-detail-content-${run.id}`}>
                              <div style={{ fontWeight: 600, marginBottom: "12px", color: "#24292f" }}>
                                Results for {runName} ({cache.data.length} total)
                              </div>
                              {groupResultsBySession(cache.data).map(([sid, sessionResults]) => (
                                <div key={sid} className="sp-run-session-block" style={{ marginBottom: "16px", background: "#fff", border: "1px solid #d0d7de", borderRadius: "6px", padding: "12px" }} data-testid={`run-session-group-${sid}`}>
                                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                                    <span style={{ fontWeight: 600, fontSize: "0.85em", color: "#24292f" }}>Session:</span>
                                    <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontSize: "0.85em", color: "#57606a" }}>
                                      {sid}
                                    </span>
                                    <span style={{ fontSize: "0.8em", color: "#6e7781" }}>
                                      ({sessionResults.length} tag{sessionResults.length === 1 ? "" : "s"})
                                    </span>
                                  </div>
                                  <table style={{ width: "100%", fontSize: "0.85em", borderCollapse: "collapse" }}>
                                    <thead>
                                      <tr style={{ borderBottom: "1px solid #e1e4e8", textAlign: "left", color: "#57606a" }}>
                                        <th style={{ padding: "4px 8px", width: "70px" }}>Time</th>
                                        <th style={{ padding: "4px 8px" }}>Quote</th>
                                        <th style={{ padding: "4px 8px" }}>Tags</th>
                                        <th style={{ padding: "4px 8px" }}>Rationale</th>
                                        <th style={{ padding: "4px 8px", width: "90px" }}>Confidence</th>
                                        <th style={{ padding: "4px 8px", width: "90px" }}>Agreement</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {sessionResults.map((r) => (
                                        <tr key={r.id} style={{ borderBottom: "1px solid #f0f0f0" }} data-testid={`run-result-item-${r.id}`}>
                                          <td style={{ padding: "6px 8px", verticalAlign: "top", fontFamily: "monospace" }}>{r.quote_timestamp}</td>
                                          <td style={{ padding: "6px 8px", verticalAlign: "top", fontStyle: "italic", maxWidth: "260px" }}>"{r.quote}"</td>
                                          <td style={{ padding: "6px 8px", verticalAlign: "top" }}>
                                            {r.tags?.length ? r.tags.join(", ") : r.skill || "—"}
                                          </td>
                                          <td style={{ padding: "6px 8px", verticalAlign: "top", color: "#57606a" }}>{r.rationale || "—"}</td>
                                          <td style={{ padding: "6px 8px", verticalAlign: "top" }}>{r.confidence || "—"}</td>
                                          <td style={{ padding: "6px 8px", verticalAlign: "top" }}>{r.existing_review?.agreement || "—"}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
